import { useMemo, useCallback } from 'react';
import { pick, equals } from 'ramda';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as Types from '../types';
import * as helpers from '../helpers';
import * as actions from '../store/actions';
import { useValidate } from './useValidate';
import { PERIODS, ANOTHER_PERIOD_VALUE } from '../constants';
import { useOnPeriodFilterChange } from './useOnPeriodFilterChange';
import { useDynamicReducerDispatch, useDynamicReducerSelector } from '../store';

export const useTableHeader = () => {
    const translate = useComponentTranslations();
    const dispatch = useDynamicReducerDispatch();
    const {
        period,
        endDate,
        startDate,
        isLoading
    } = useDynamicReducerSelector(pick([
        'period',
        'endDate',
        'startDate',
        'isLoading'
    ]));
    const headerInitialValues = useMemo(() => ({
        period,
        from: startDate,
        until: endDate
    }), [period, startDate, endDate]);

    const headerValidate = useValidate();
    const onPeriodChange = useOnPeriodFilterChange();

    const getIsDisabled = useCallback(({ from, until }: Types.IDates): boolean => {
        return isLoading
            || equals(startDate, from)
            && equals(endDate, until)
            || equals(startDate, helpers.intoDate(from))
            && equals(endDate, helpers.intoDate(until))
            || equals(helpers.startOfDay(startDate), helpers.startOfDay(from as Date))
            && equals(helpers.startOfDay(endDate), helpers.startOfDay(until as Date));
    }, [isLoading, startDate, endDate]);

    const anotherPeriodFilter = useMemo(() => {
        return period !== ANOTHER_PERIOD_VALUE ? undefined : {
            from: { label: translate('filter.from'), disabled: isLoading },
            until: { label: translate('filter.until'), disabled: isLoading },
            submit: {
                title: translate('filter.apply'),
                getIsDisabled
            }
        };
    }, [translate, getIsDisabled, period, isLoading]);

    const onAnotherPeriodApply = useCallback((values: Types.IDates) => {
        dispatch(actions.setData({
            startDate: helpers.startOfDay(values.from as Date),
            endDate: helpers.endOfDay(values.until as Date),
            page: { pageId: 1, isPageChanged: false }
        }));
    }, [dispatch]);

    const periodOptions = useMemo(() => [
        ...PERIODS.map((item) => ({
            ...item,
            label: translate(item.label)
        }))
    ], [translate]);

    return useMemo(() => ({
        filters: [{
            id: 0,
            name: 'period',
            disabled: isLoading,
            options: periodOptions,
            onChange: onPeriodChange,
            label: translate('filter.title.period'),
            'aria-label': translate(['filter.title.period', 'playtech.mobile.system.select.aria-label'])
        }],
        headerValidate,
        headerInitialValues,
        anotherPeriodFilter,
        onAnotherPeriodApply
    }), [
        translate, periodOptions, headerValidate,
        isLoading, headerInitialValues, onPeriodChange,
        anotherPeriodFilter, onAnotherPeriodApply
    ]);
};
